import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Layout from '@/components/Layout';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  RefreshCw,
  Download,
  Filter,
  Edit2,
  Trash2,
  MoreVertical,
  HelpCircle,
  Zap,
  CheckCircle2,
  X,
  CreditCard,
  Lock,
  Unlock,
  Package,
  ChevronDown,
  ChevronUp,
  Plus,
  Share2,
  Copy,
  ArrowRightLeft,
  ArrowLeftRight,
  PieChart,
  AlertTriangle,
  Search,
  FileText,
  FileSpreadsheet,
  ArrowUpDown,
  Check
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import { getTodayLocalDateString, formatLocalDate } from '@/utils/dateUtils';
import TransactionModal from '@/components/TransactionModal';
import CreditCardBreakdownModal from '@/components/CreditCardBreakdownModal';
import MobileBottomSheet from '@/components/MobileBottomSheet';
import { exportToPDF, exportToExcel, exportToCSV } from '@/utils/exportUtils';
import axios from 'axios';

import BACKEND_URL from '@/config/backendUrl';


const AccountDetails = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [showAdjustBalance, setShowAdjustBalance] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [asRealYield, setAsRealYield] = useState(true);
  const [isSubmittingAdjust, setIsSubmittingAdjust] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null); // Para controlar qué tooltip mostrar
  // Estados para Registrar Rendimiento Real (Ajuste Silencioso)
  const [showRealYieldModal, setShowRealYieldModal] = useState(false);
  const [realYieldAmount, setRealYieldAmount] = useState('');
  const [realYieldDate, setRealYieldDate] = useState(getTodayLocalDateString());
  const [realYieldResult, setRealYieldResult] = useState(null);
  const [isSubmittingYield, setIsSubmittingYield] = useState(false);
  // Phase 2: Pestañas para tarjeta de crédito
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'msi' | 'retentions'
  const [releasingRetentionId, setReleasingRetentionId] = useState(null);
  const [expandedBreakdownId, setExpandedBreakdownId] = useState(null);
  // Modal de Transacción directo desde la cuenta
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [modalPreselectedType, setModalPreselectedType] = useState('expense');
  const [modalInitialMsi, setModalInitialMsi] = useState(false);
  const [modalInitialRetention, setModalInitialRetention] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // Modal Exclusivo de Desglose de Tarjeta
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [breakdownModalInitialMode, setBreakdownModalInitialMode] = useState('normal');
  const [selectedTxForActions, setSelectedTxForActions] = useState(null);

  // Estados para Filtros, Búsqueda y Descarga/Exportación
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all' | 'expense' | 'income'
  const [sortBy, setSortBy] = useState('date-desc'); // 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc'

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token && accountId) {
      sessionStorage.setItem('return_to_account_id', accountId);
      fetchAccountDetails();
      fetchAccountTransactions();
      fetchCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, accountId, fetchCategories]);

  // Listener para refrescar datos de la cuenta automáticamente en tiempo real
  useEffect(() => {
    const handleTransactionAdded = () => {
      if (token && accountId) {
        fetchAccountDetails();
        fetchAccountTransactions();
      }
    };
    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => window.removeEventListener('transaction-added', handleTransactionAdded);
  }, [token, accountId]);

  // Bloquear scroll de fondo cuando los modales de rendimiento o ajuste de saldo estén abiertos
  useEffect(() => {
    if (showRealYieldModal || showAdjustBalance) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showRealYieldModal, showAdjustBalance]);

  const fetchAccountDetails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar cuenta');
      }

      const data = await response.json();
      setAccount(data);
    } catch (error) {
      console.error('Error fetching account:', error);
      showToast.error('Error al cargar cuenta');
      navigate('/accounts');
    }
  };

  const fetchAccountTransactions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/transactions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar transacciones');
      }

      const data = await response.json();
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showToast.error('Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshBalance = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al actualizar saldo');
      }

      const data = await response.json();
      setAccount(prev => ({
        ...prev,
        current_balance: data.current_balance,
        available_credit: data.available_credit
      }));
      showToast.success('Saldo actualizado');
    } catch (error) {
      console.error('Error refreshing balance:', error);
      showToast.error(error.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAdjustBalance = async () => {
    if (!newBalance || newBalance === '') {
      showToast.error('Por favor ingresa un nuevo saldo');
      return;
    }

    let balanceValue = parseFloat(newBalance);
    if (isNaN(balanceValue)) {
      showToast.error('El saldo debe ser un número válido');
      return;
    }

    if (account?.type === 'credit_card') {
      // Para tarjeta de crédito, la deuda es un saldo negativo
      balanceValue = -1 * Math.abs(balanceValue);
    }

    const isYieldAcc = Boolean(account?.has_yield);
    const useRealYield = isYieldAcc && asRealYield;
    const defaultReason = useRealYield ? 'Rendimiento' : '';

    setIsSubmittingAdjust(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/adjust-balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_balance: balanceValue,
          reason: adjustReason.trim() || defaultReason,
          as_real_yield: useRealYield
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al ajustar saldo');
      }

      const data = await response.json();
      showToast.success(data.message);
      setShowAdjustBalance(false);
      setNewBalance('');
      setAdjustReason('');
      setAsRealYield(Boolean(account?.has_yield));

      if (data.is_real_yield) {
        setRealYieldResult(data);
        setShowRealYieldModal(true);
      }

      // Refresh account and transactions
      await fetchAccountDetails();
      await fetchAccountTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
      window.dispatchEvent(new CustomEvent('account-updated'));
    } catch (error) {
      console.error('Error adjusting balance:', error);
      showToast.error(error.message);
    } finally {
      setIsSubmittingAdjust(false);
    }
  };

  const handleRecordRealYield = async (e) => {
    e.preventDefault();
    const amountVal = parseFloat(realYieldAmount);
    if (isNaN(amountVal) || amountVal <= 0) {
      showToast.error('Por favor ingresa un monto válido mayor a cero');
      return;
    }

    setIsSubmittingYield(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/record-real-yield`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amountVal,
          date: realYieldDate
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al registrar rendimiento real');
      }

      const data = await response.json();
      setRealYieldResult(data);
      showToast.success('Rendimiento real registrado correctamente');
      await fetchAccountDetails();
      await fetchAccountTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
      window.dispatchEvent(new CustomEvent('account-updated'));
    } catch (error) {
      console.error('Error recording real yield:', error);
      showToast.error(error.message);
    } finally {
      setIsSubmittingYield(false);
    }
  };

  const handleReleaseRetention = async (transactionId) => {
    setReleasingRetentionId(transactionId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/transactions/${transactionId}/release-retention`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Error al liberar retención');
      }
      showToast.success('Retención liberada. Tu saldo disponible se ha actualizado.');
      await fetchAccountDetails();
      await fetchAccountTransactions();
    } catch (error) {
      console.error('Error releasing retention:', error);
      showToast.error(error.message);
    } finally {
      setReleasingRetentionId(null);
    }
  };

  const handleConvertToCharge = async (transactionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/transactions/${transactionId}/convert-to-charge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al convertir retención a cobro');
      showToast.success('Retención convertida a cobro definitivo de la tarjeta');
      await fetchAccountDetails();
      await fetchAccountTransactions();
    } catch (error) {
      console.error('Error converting retention:', error);
      showToast.error(error.message);
    }
  };

  const handleToggleBreakdown = async (transactionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/transactions/${transactionId}/toggle-breakdown`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cambiar tipo de movimiento');
      const data = await response.json();
      showToast.success(data.message);
      await fetchAccountDetails();
      await fetchAccountTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (error) {
      console.error('Error toggling breakdown:', error);
      showToast.error(error.message);
    }
  };

  const formatCurrency = (amount, currency = 'MXN') => {
    const symbol = currency === 'USD' ? '$' : '$';
    return `${symbol}${Math.abs(amount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatLocalDate(dateString, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTransactionIcon = (type) => {
    if (type === 'income') return <TrendingUp className="w-5 h-5 text-green-600" />;
    if (type === 'expense') return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <DollarSign className="w-5 h-5 text-blue-600" />;
  };

  const getTransactionColor = (type) => {
    if (type === 'income') return 'text-green-600';
    if (type === 'expense') return 'text-red-600';
    return 'text-blue-600';
  };

  const isAdjustment = (t) => Boolean(t.is_balance_adjustment) || 
                              /ajuste\s+de\s+saldo/i.test(t.description || '') || 
                              (t.notes && /saldo\s+anterior/i.test(t.notes));

  const getAdjustmentDetails = (tx) => {
    if (!isAdjustment(tx)) return null;
    let prev = tx.previous_balance;
    let curr = tx.new_balance;
    if ((prev === undefined || prev === null || curr === undefined || curr === null) && tx.notes) {
      const match = tx.notes.match(/saldo anterior:\s*([-$0-9.,]+)[,\s→-]+(?:nuevo saldo:\s*)?([-$0-9.,]+)/i);
      if (match) {
        prev = parseFloat(match[1].replace(/[$]/g, ''));
        curr = parseFloat(match[2].replace(/[$]/g, ''));
      }
    }
    return { prev, curr, notes: tx.notes };
  };

  // Calcular saldos antes y después cronológicos para TODOS los movimientos de la cuenta
  const transactionBalancesMap = useMemo(() => {
    if (!account || !transactions || transactions.length === 0) return {};
    const map = {};

    // Ordenar cronológicamente (más antiguo primero)
    const sorted = [...transactions].sort((a, b) => {
      const dayA = (a.date || a.created_at || '').slice(0, 10);
      const dayB = (b.date || b.created_at || '').slice(0, 10);
      if (dayA !== dayB) return dayA.localeCompare(dayB);
      const createA = new Date(a.created_at || a.date || 0).getTime();
      const createB = new Date(b.created_at || b.date || 0).getTime();
      return createA - createB;
    });

    const isCreditCard = account.type === 'credit_card';
    let running = parseFloat(account.initial_balance) || 0;
    if (isCreditCard && running > 0) {
      running = -running;
    }

    for (const tx of sorted) {
      if (isAdjustment(tx)) {
        let prev = running;
        let next = tx.new_balance;
        if ((next === undefined || next === null) && tx.notes) {
          const match = tx.notes.match(/(?:nuevo saldo|saldo despu[ée]s):\s*([-$0-9.,]+)/i);
          if (match) {
            next = parseFloat(match[1].replace(/[$]/g, ''));
          }
        }
        if (next !== undefined && !isNaN(next)) {
          const p = isCreditCard ? -Math.abs(prev) : prev;
          const n = isCreditCard ? -Math.abs(next) : next;
          map[tx.id] = { prev: p, next: n };
          running = n;
          continue;
        }
      }

      const prev = running;
      const amt = parseFloat(tx.amount) || 0;
      let next = prev;

      if (tx.is_retention && tx.retention_status === 'released') {
        next = prev;
      } else if (tx.is_breakdown) {
        next = prev;
      } else if (tx.type === 'income') {
        // En crédito: abono/pago suma al saldo contable (acerca a 0, reduce deuda)
        next = prev + amt;
      } else if (tx.type === 'expense') {
        // En crédito: gasto resta al saldo contable (aleja de 0, aumenta deuda)
        next = prev - amt;
      }

      map[tx.id] = { prev, next };
      running = next;
    }

    return map;
  }, [account, transactions]);

  const calculateStats = () => {
    const income = transactions
      .filter(t => t.type === 'income' && !isAdjustment(t))
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

    const expenses = transactions
      .filter(t => t.type === 'expense' && !t.is_breakdown && !isAdjustment(t))
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

    return { income, expenses };
  };

  // Punto 8 y 12: Métricas del Desglose de Deuda de la Tarjeta
  const isCreditCard = account?.type === 'credit_card';
  // Deuda actual de la tarjeta (siempre monto positivo representativo de lo que se debe)
  const totalDebt = isCreditCard ? Math.abs(parseFloat(account?.current_balance) || 0) : 0;
  // Total de movimientos desglosados en la tarjeta (excluyendo retenciones liberadas y ajustes de saldo)
  const totalDesglosado = isCreditCard 
    ? transactions
        .filter(t => (t.is_breakdown || t.type === 'expense') && !isAdjustment(t) && t.retention_status !== 'released')
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
    : 0;
  const remainingToBreakdown = Math.max(0, totalDebt - totalDesglosado);
  const excessBreakdown = Math.max(0, totalDesglosado - totalDebt);
  const percentageBrokenDown = totalDebt > 0 
    ? Math.min(100, Math.round((totalDesglosado / totalDebt) * 100)) 
    : (totalDesglosado > 0 ? 100 : 0);
  const isBreakdownExact = totalDebt > 0 && Math.abs(totalDesglosado - totalDebt) < 0.01;
  const isBreakdownExceeded = totalDebt > 0 && (totalDesglosado > totalDebt + 0.01);

  // Cálculo Inteligente de Pago del Mes (Para no generar intereses)
  const activeMsiList = isCreditCard ? transactions.filter(t => t.is_msi && ((t.msi_months_paid || 0) < (t.msi_months || 1))) : [];
  const msiMonthPayment = activeMsiList.reduce((sum, t) => sum + (parseFloat(t.msi_monthly_payment) || (parseFloat(t.amount) / (t.msi_months || 1))), 0);
  const totalMsiRemainingDebt = activeMsiList.reduce((sum, t) => {
    const paid = (t.msi_months_paid || 0) * (parseFloat(t.msi_monthly_payment) || (t.amount / (t.msi_months || 1)));
    return sum + Math.max(0, parseFloat(t.amount) - paid);
  }, 0);

  // Retenciones activas no vencidas (no exigibles este mes a menos que venzan en el mes en curso o antes)
  const activeRetentionsNotDue = isCreditCard
    ? transactions.filter(t => {
        if (!t.is_retention || t.retention_status === 'released') return false;
        if (!t.retention_expected_release_date) return true; // retención temporal sin fecha fija -> no exigible este mes
        const currentYearMonth = getTodayLocalDateString().slice(0, 7); // ej "2026-09"
        const retentionYearMonth = t.retention_expected_release_date.slice(0, 7);
        return retentionYearMonth > currentYearMonth;
      })
    : [];
  const activeRetentionsNotDueDebt = activeRetentionsNotDue.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const nonMsiDebt = Math.max(0, totalDebt - totalMsiRemainingDebt - activeRetentionsNotDueDebt);
  const suggestedMonthlyPayment = totalDebt > 0 ? Math.min(totalDebt, nonMsiDebt + msiMonthPayment) : 0;
  const isCustomStatementAmount = account?.monthly_statement_amount !== undefined && account?.monthly_statement_amount !== null;
  const effectiveMonthlyPayment = isCustomStatementAmount
    ? parseFloat(account.monthly_statement_amount)
    : suggestedMonthlyPayment;
  const effectiveCyclePurchases = isCustomStatementAmount
    ? Math.max(0, effectiveMonthlyPayment - msiMonthPayment)
    : nonMsiDebt;

  // Estados Modal Ajustar Pago Mensual / Fechas
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementCutDay, setStatementCutDay] = useState('');
  const [statementDueDay, setStatementDueDay] = useState('');
  const [statementOffsetMonths, setStatementOffsetMonths] = useState(0);
  const [statementMonthlyAmount, setStatementMonthlyAmount] = useState('');
  const [statementEnableReminder, setStatementEnableReminder] = useState(true);
  const [statementReminderDays, setStatementReminderDays] = useState(3);
  const [isSubmittingStatement, setIsSubmittingStatement] = useState(false);

  // Inicializar campos del modal al abrir
  useEffect(() => {
    if (account && showStatementModal) {
      setStatementCutDay(account.cut_day !== undefined && account.cut_day !== null ? String(account.cut_day) : '');
      setStatementDueDay(account.payment_due_day !== undefined && account.payment_due_day !== null ? String(account.payment_due_day) : '');
      setStatementOffsetMonths(account.payment_due_offset_months !== undefined && account.payment_due_offset_months !== null ? Number(account.payment_due_offset_months) : 0);
      const curStatementAmt = account.monthly_statement_amount !== undefined && account.monthly_statement_amount !== null ? parseFloat(account.monthly_statement_amount) : null;
      if (curStatementAmt !== null) {
        setStatementMonthlyAmount(formatCurrencyInput(String(curStatementAmt)));
      } else {
        setStatementMonthlyAmount(suggestedMonthlyPayment > 0 ? formatCurrencyInput(String(suggestedMonthlyPayment.toFixed(2))) : '');
      }
      setStatementEnableReminder(account.auto_reminder !== false);
      setStatementReminderDays(account.monthly_reminder_days_before || 3);
    }
  }, [showStatementModal, account, suggestedMonthlyPayment]);

  const handleSaveStatement = async (e) => {
    e && e.preventDefault();
    setIsSubmittingStatement(true);
    try {
      const parsedAmount = statementMonthlyAmount ? parseCurrency(statementMonthlyAmount) : null;
      const res = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/credit-card-statement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          cut_day: statementCutDay ? parseInt(statementCutDay, 10) : null,
          payment_due_day: statementDueDay ? parseInt(statementDueDay, 10) : null,
          payment_due_offset_months: parseInt(statementOffsetMonths, 10) || 0,
          monthly_statement_amount: parsedAmount,
          monthly_statement_unbroken_adj: 0.0,
          auto_reminder: statementEnableReminder,
          reminder_days_before: statementReminderDays
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar estado de cuenta');
      }

      const data = await res.json();
      showToast.success(data.reminder_status ? `${data.message} (${data.reminder_status})` : data.message);
      setShowStatementModal(false);
      await fetchAccountDetails();
      await fetchAccountTransactions();
    } catch (err) {
      console.error('Error saving statement:', err);
      showToast.error(err.message || 'Error al guardar');
    } finally {
      setIsSubmittingStatement(false);
    }
  };

  // Estados Modal Pagar Tarjeta
  const [isPayCardModalOpen, setIsPayCardModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [paySourceType, setPaySourceType] = useState('transfer'); // 'transfer' | 'direct'
  const [paySourceAccountId, setPaySourceAccountId] = useState('');
  const [payAdvanceMsi, setPayAdvanceMsi] = useState(true);
  const [isSubmittingPay, setIsSubmittingPay] = useState(false);
  const [bankAccountsList, setBankAccountsList] = useState([]);

  // Touch drag para modal pagar tarjeta
  const [payDragCurrentY, setPayDragCurrentY] = useState(0);
  const payDragStartY = useRef(0);
  const isPayDragging = useRef(false);
  const isPayFromHandle = useRef(false);
  const payScrollRef = useRef(null);

  const handlePayTouchStart = (e, fromHandle = false) => {
    isPayFromHandle.current = fromHandle;
    const isAtTop = !payScrollRef.current || payScrollRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      payDragStartY.current = e.touches[0].clientY;
      isPayDragging.current = true;
    }
  };

  const handlePayTouchMove = (e) => {
    if (!isPayDragging.current) return;
    const diff = e.touches[0].clientY - payDragStartY.current;
    const isAtTop = !payScrollRef.current || payScrollRef.current.scrollTop <= 0;
    if (diff > 0 && (isPayFromHandle.current || isAtTop)) {
      setPayDragCurrentY(diff);
    } else if (diff < 0) {
      isPayDragging.current = false;
      setPayDragCurrentY(0);
    }
  };

  const handlePayTouchEnd = () => {
    if (!isPayDragging.current) return;
    isPayDragging.current = false;
    const threshold = isPayFromHandle.current ? 65 : 120;
    if (payDragCurrentY > threshold) {
      setIsPayCardModalOpen(false);
    }
    setPayDragCurrentY(0);
    payDragStartY.current = 0;
    isPayFromHandle.current = false;
  };

  // Touch drag para modal Ajustar Pago del Mes y Fechas (showStatementModal)
  const [statementDragCurrentY, setStatementDragCurrentY] = useState(0);
  const statementDragStartY = useRef(0);
  const isStatementDragging = useRef(false);
  const isStatementFromHandle = useRef(false);
  const statementScrollRef = useRef(null);

  const handleStatementTouchStart = (e, fromHandle = false) => {
    isStatementFromHandle.current = fromHandle;
    const isAtTop = !statementScrollRef.current || statementScrollRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      statementDragStartY.current = e.touches[0].clientY;
      isStatementDragging.current = true;
    }
  };

  const handleStatementTouchMove = (e) => {
    if (!isStatementDragging.current) return;
    const diff = e.touches[0].clientY - statementDragStartY.current;
    const isAtTop = !statementScrollRef.current || statementScrollRef.current.scrollTop <= 0;
    if (diff > 0 && (isStatementFromHandle.current || isAtTop)) {
      setStatementDragCurrentY(diff);
    } else if (diff < 0) {
      isStatementDragging.current = false;
      setStatementDragCurrentY(0);
    }
  };

  const handleStatementTouchEnd = () => {
    if (!isStatementDragging.current) return;
    isStatementDragging.current = false;
    const threshold = isStatementFromHandle.current ? 65 : 120;
    if (statementDragCurrentY > threshold) {
      setShowStatementModal(false);
    }
    setStatementDragCurrentY(0);
    statementDragStartY.current = 0;
    isStatementFromHandle.current = false;
  };

  // Touch drag para modal Ajustar Saldo
  const [adjustDragCurrentY, setAdjustDragCurrentY] = useState(0);
  const adjustDragStartY = useRef(0);
  const isAdjustDragging = useRef(false);
  const isAdjustFromHandle = useRef(false);

  const handleAdjustTouchStart = (e, fromHandle = false) => {
    isAdjustFromHandle.current = fromHandle;
    adjustDragStartY.current = e.touches[0].clientY;
    isAdjustDragging.current = true;
  };

  const handleAdjustTouchMove = (e) => {
    if (!isAdjustDragging.current) return;
    const diff = e.touches[0].clientY - adjustDragStartY.current;
    if (diff > 0) {
      setAdjustDragCurrentY(diff);
    } else if (diff < 0) {
      isAdjustDragging.current = false;
      setAdjustDragCurrentY(0);
    }
  };

  const handleAdjustTouchEnd = () => {
    if (!isAdjustDragging.current) return;
    isAdjustDragging.current = false;
    const threshold = isAdjustFromHandle.current ? 65 : 120;
    if (adjustDragCurrentY > threshold) {
      setShowAdjustBalance(false);
      setNewBalance('');
      setAdjustReason('');
      setAsRealYield(true);
    }
    setAdjustDragCurrentY(0);
    adjustDragStartY.current = 0;
    isAdjustFromHandle.current = false;
  };

  // Touch drag para modal Rendimiento Real
  const [yieldDragCurrentY, setYieldDragCurrentY] = useState(0);
  const yieldDragStartY = useRef(0);
  const isYieldDragging = useRef(false);
  const isYieldFromHandle = useRef(false);

  const handleYieldTouchStart = (e, fromHandle = false) => {
    isYieldFromHandle.current = fromHandle;
    yieldDragStartY.current = e.touches[0].clientY;
    isYieldDragging.current = true;
  };

  const handleYieldTouchMove = (e) => {
    if (!isYieldDragging.current) return;
    const diff = e.touches[0].clientY - yieldDragStartY.current;
    if (diff > 0) {
      setYieldDragCurrentY(diff);
    } else if (diff < 0) {
      isYieldDragging.current = false;
      setYieldDragCurrentY(0);
    }
  };

  const handleYieldTouchEnd = () => {
    if (!isYieldDragging.current) return;
    isYieldDragging.current = false;
    const threshold = isYieldFromHandle.current ? 65 : 120;
    if (yieldDragCurrentY > threshold) {
      setShowRealYieldModal(false);
      setRealYieldResult(null);
    }
    setYieldDragCurrentY(0);
    yieldDragStartY.current = 0;
    isYieldFromHandle.current = false;
  };

  // Bloquear scroll de body cuando cualquier modal esté abierto
  useEffect(() => {
    const isModalOpen = isPayCardModalOpen || showStatementModal || showAdjustBalance || showRealYieldModal || isBreakdownModalOpen;
    if (isModalOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevOverscroll = document.body.style.overscrollBehavior;
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
      return () => {
        document.body.style.overflow = prevOverflow || '';
        document.body.style.overscrollBehavior = prevOverscroll || '';
      };
    }
  }, [isPayCardModalOpen, showStatementModal, showAdjustBalance, showRealYieldModal, isBreakdownModalOpen]);

  // Cargar cuentas bancarias para el pago
  useEffect(() => {
    if (token && isCreditCard) {
      const fetchBanks = async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/accounts`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const banks = res.data.filter(a => a.id !== accountId && (a.type === 'bank' || a.type === 'digital_wallet' || a.type === 'cash'));
          setBankAccountsList(banks);
          if (banks.length > 0 && !paySourceAccountId) {
            const defaultBank = banks.find(b => b.is_default) || banks[0];
            setPaySourceAccountId(defaultBank.id);
          }
        } catch {}
      };
      fetchBanks();
    }
  }, [token, isCreditCard, accountId]);

  // Al abrir modal de pago, auto-seleccionar monto sugerido del mes
  useEffect(() => {
    if (isPayCardModalOpen) {
      const defaultAmt = effectiveMonthlyPayment > 0 ? effectiveMonthlyPayment : totalDebt;
      setPayAmount(defaultAmt > 0 ? defaultAmt.toFixed(2) : '');
      setPayAdvanceMsi(activeMsiList.length > 0);
    }
  }, [isPayCardModalOpen, effectiveMonthlyPayment, totalDebt, activeMsiList.length]);

  const handlePayCreditCard = async (e) => {
    e && e.preventDefault();
    const parsedPayAmount = parseFloat(payAmount);
    if (!parsedPayAmount || parsedPayAmount <= 0) {
      showToast.error('Ingresa un monto válido para pagar');
      return;
    }

    if (paySourceType === 'transfer' && !paySourceAccountId) {
      showToast.error('Selecciona la cuenta de origen del pago');
      return;
    }

    setIsSubmittingPay(true);
    try {
      if (paySourceType === 'transfer') {
        await axios.post(`${BACKEND_URL}/api/transfers`, {
          from_account_id: paySourceAccountId,
          to_account_id: account.id,
          amount: parsedPayAmount,
          description: `Pago de Tarjeta: ${account.name}`,
          date: getTodayLocalDateString()
        }, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/credit-card-payment`, {
          credit_card_account_id: account.id,
          amount: parsedPayAmount,
          description: `Abono a ${account.name}`,
          date: getTodayLocalDateString()
        }, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
      }

      // Si se activó avanzar MSI y hay cuotas activas
      if (payAdvanceMsi && activeMsiList.length > 0) {
        for (const msiTx of activeMsiList) {
          try {
            const nextPaid = Math.min(msiTx.msi_months || 1, (msiTx.msi_months_paid || 0) + 1);
            await axios.put(`${BACKEND_URL}/api/transactions/${msiTx.id}`, {
              msi_months_paid: nextPaid
            }, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              }
            });
          } catch {}
        }
      }

      showToast.success(`Pago de $${parsedPayAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} aplicado exitosamente`);
      setIsPayCardModalOpen(false);
      await fetchAccountDetails();
      await fetchAccountTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (err) {
      console.error('Error pagando tarjeta:', err);
      showToast.error(err.message || 'No se pudo aplicar el pago');
    } finally {
      setIsSubmittingPay(false);
    }
  };

  const handleOpenTransactionModal = (type = 'expense', isMsi = false, isRetention = false, tx = null) => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    setModalPreselectedType(type);
    setModalInitialMsi(isMsi);
    setModalInitialRetention(isRetention);
    setEditingTransaction(tx);
    setIsTransactionModalOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    if (isCreditCard || transaction.is_breakdown || transaction.is_retention || transaction.is_msi) {
      setEditingTransaction(transaction);
      setBreakdownModalInitialMode(transaction.is_retention ? 'retention' : (transaction.is_msi ? 'msi' : 'normal'));
      setIsBreakdownModalOpen(true);
    } else {
      handleOpenTransactionModal(transaction.type, transaction.is_msi || false, transaction.is_retention || false, transaction);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${transactionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      showToast.success('Transacción eliminada exitosamente');
      setShowDeleteConfirm(false);
      setTransactionToDelete(null);

      // Refresh data
      await fetchAccountDetails();
      await fetchAccountTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (error) {
      console.error('Error deleting transaction:', error);
      showToast.error(error.response?.data?.detail || error.message || 'Error al eliminar transacción');
    }
  };

  const handleExport = (format, listToExport) => {
    try {
      const exportData = listToExport && listToExport.length > 0 ? listToExport : transactions;
      if (!exportData || exportData.length === 0) {
        showToast.error('No hay movimientos para descargar');
        return;
      }
      const accName = account?.name || 'Cuenta';
      const userName = user?.name || user?.email || 'Usuario';

      if (format === 'pdf') {
        exportToPDF(exportData, categories, null, null, `${userName} - ${accName}`);
        showToast.success('Reporte PDF descargado exitosamente');
      } else if (format === 'excel') {
        exportToExcel(exportData, categories, null, null, userName, `Movimientos - ${accName}`);
        showToast.success('Archivo Excel (.xls) descargado exitosamente');
      } else if (format === 'csv') {
        exportToCSV(exportData, categories);
        showToast.success('Archivo CSV descargado exitosamente');
      }
      setShowExportModal(false);
    } catch (err) {
      console.error('Error al exportar movimientos:', err);
      showToast.error('Error al generar la descarga');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  if (!account) {
    return null;
  }

  const stats = calculateStats();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 md:p-6 pb-28 sm:pb-12">
        {/* Header */}
        <button
          onClick={() => {
            if (accountId) {
              sessionStorage.setItem('return_to_account_id', accountId);
            }
            navigate('/accounts');
          }}
          className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a Cuentas
        </button>

        {/* Account Card */}
        <div
          className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 mb-6 text-white"
          style={{
            background: `linear-gradient(135deg, ${account.color} 0%, ${account.color}dd 100%)`
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-1">{account.name}</h1>
              <p className="text-sm opacity-80">{account.type}</p>
            </div>
            <div className="flex items-center gap-2">
              {account.has_yield && (
                <button
                  onClick={() => {
                    setRealYieldAmount('');
                    setRealYieldDate(getTodayLocalDateString());
                    setRealYieldResult(null);
                    setShowRealYieldModal(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold rounded-lg text-xs shadow-md transition-all hover:scale-105"
                  title="Registrar el rendimiento que realmente depositó el banco"
                >
                  <Zap className="w-4 h-4 text-amber-950 fill-amber-950" />
                  <span className="hidden sm:inline">Rendimiento Real</span>
                  <span className="sm:hidden">Rendimiento</span>
                </button>
              )}
              <button
                onClick={() => {
                  setAsRealYield(Boolean(account?.has_yield));
                  setShowAdjustBalance(true);
                }}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Ajustar saldo"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={handleRefreshBalance}
                disabled={refreshing}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Mostrar saldo según el tipo de cuenta */}
          {account.type === 'credit_card' && account.credit_limit ? (
            <div className="space-y-4">
              {/* Saldo Actual (Deuda) */}
              <div className="pb-3 border-b border-white/20">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm opacity-80">Saldo Actual (Deuda)</p>
                  <div className="relative">
                    <HelpCircle
                      className="w-4 h-4 opacity-60 cursor-help"
                      onMouseEnter={() => setShowTooltip('debt')}
                      onMouseLeave={() => setShowTooltip(null)}
                    />
                    {showTooltip === 'debt' && (
                      <div className="absolute left-6 top-0 bg-slate-900 text-white text-xs p-2 rounded-lg w-48 z-10 shadow-lg">
                        Lo que actualmente debes al banco
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-4xl font-bold">
                  {account.current_balance < 0 ? '-' : ''}{formatCurrency(Math.abs(account.current_balance), account.currency)}
                </p>
              </div>

              {/* Saldo Disponible */}
              <div className="pb-3 border-b border-white/20">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm opacity-80">Saldo Disponible</p>
                  <div className="relative">
                    <HelpCircle
                      className="w-4 h-4 opacity-60 cursor-help"
                      onMouseEnter={() => setShowTooltip('available')}
                      onMouseLeave={() => setShowTooltip(null)}
                    />
                    {showTooltip === 'available' && (
                      <div className="absolute left-6 top-0 bg-slate-900 text-white text-xs p-2 rounded-lg w-48 z-10 shadow-lg">
                        Cuánto puedes gastar todavía
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-2xl font-bold">
                  {formatCurrency(account.credit_limit - Math.abs(account.current_balance), account.currency)}
                </p>
              </div>

              {/* Límite de Crédito */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm opacity-80">Límite de Crédito</p>
                  <div className="relative">
                    <HelpCircle
                      className="w-4 h-4 opacity-60 cursor-help"
                      onMouseEnter={() => setShowTooltip('limit')}
                      onMouseLeave={() => setShowTooltip(null)}
                    />
                    {showTooltip === 'limit' && (
                      <div className="absolute left-6 top-0 bg-slate-900 text-white text-xs p-2 rounded-lg w-48 z-10 shadow-lg">
                        Tu límite total de crédito
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(account.credit_limit, account.currency)}</p>
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <p className="text-sm opacity-80 mb-1">Saldo Actual</p>
              <p className="text-4xl font-bold">{formatCurrency(account.current_balance, account.currency)}</p>
            </div>
          )}

          {/* Desglose de Rendimientos para cuentas con rendimiento */}
          {account.has_yield && (
            <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                <p className="text-[11px] opacity-75 font-semibold">Tasa Anual Contratada</p>
                <p className="text-lg font-black text-white">{account.annual_yield_rate || 0}% anual</p>
                <p className="text-[10px] opacity-70 mt-0.5">Tasa prometida por la institución</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                <p className="text-[11px] opacity-75 font-semibold">Rendimiento Estimado Acumulado</p>
                <p className="text-lg font-black text-amber-300">
                  +{formatCurrency(account.accrued_yield || 0, account.currency)}
                </p>
                <p className="text-[10px] opacity-70 mt-0.5">
                  {(account.accrued_yield || 0) > 0 
                    ? 'Calculado diario con interés simple' 
                    : `Generando ~$${(((parseFloat(account.current_balance || 0) * (parseFloat(account.annual_yield_rate || 0) / 100)) / 365) || 0).toFixed(2)}/día`}
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                <p className="text-[11px] opacity-75 font-semibold">Rendimiento Real Depositado</p>
                <p className="text-lg font-black text-emerald-300">
                  {formatCurrency(account.real_yield_registered || 0, account.currency)}
                </p>
                <p className="text-[10px] opacity-70 mt-0.5">
                  {account.real_yield_date ? `Último: ${formatDate(account.real_yield_date)}` : 'Sin registros aún'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm text-slate-600 dark:text-slate-400">Ingresos</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.income, account.currency)}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-sm text-slate-600 dark:text-slate-400">Gastos</span>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(stats.expenses, account.currency)}
            </p>
          </div>
        </div>

        {/* Punto 42: Control de Pagos Mensuales, Desglose y Recordatorios de Tarjeta */}
        {account.type === 'credit_card' && (
          <div className="mb-4 p-4 sm:p-5 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-2xl text-white shadow-md border border-indigo-500/20 space-y-3.5 relative overflow-hidden">
            <div className="absolute -right-12 -top-12 w-36 h-36 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center shrink-0 text-indigo-300">
                  <Calendar className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xs sm:text-sm font-bold tracking-tight">Pago del Mes (Para no generar intereses)</h3>
                    {account.cut_day ? (() => {
                      const now = new Date();
                      const d = new Date(now.getFullYear(), now.getMonth(), account.cut_day);
                      if (d <= now) d.setMonth(d.getMonth() + 1);
                      const label = d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                      return (
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-500/40 text-slate-200 border border-slate-400/30">
                          ✂️ Corte: {label}
                        </span>
                      );
                    })() : null}
                    {account.payment_due_day ? (() => {
                      const now = new Date();
                      const cutDay = account.cut_day || 1;
                      let cutDate = new Date(now.getFullYear(), now.getMonth(), cutDay);
                      if (cutDate <= now) cutDate.setMonth(cutDate.getMonth() + 1);
                      const offset = account.payment_due_offset_months || 0;
                      const dueDate = new Date(cutDate.getFullYear(), cutDate.getMonth() + offset, account.payment_due_day);
                      const label = dueDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                      const offsetLabel = offset === 1 ? ' (+1 mes)' : offset === 2 ? ' (+2 meses)' : '';
                      return (
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                          Vence: {label}{offsetLabel}
                        </span>
                      );
                    })() : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/30">
                        Sin fecha configurada
                      </span>
                    )}
                    {account.auto_reminder && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex items-center gap-1">
                        🔔 Recordatorio activo
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-300">
                    Cuotas de MSI activas + compras del ciclo cubiertas
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() => setShowStatementModal(true)}
                  className="py-2 px-3 bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-all border border-white/10"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Ajustar / Fechas</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsPayCardModalOpen(true)}
                  className="py-2 px-3.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-black rounded-xl text-xs flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 transition-all"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span>Pagar Tarjeta</span>
                </button>
              </div>
            </div>

            {/* Monto Prominente y Desglose de Facturación */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1 relative z-10">
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col justify-center">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-indigo-300 uppercase tracking-wider block">
                    Total a Pagar
                  </span>
                  {isCustomStatementAmount && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 border border-amber-400/30">
                      Edo. Cuenta
                    </span>
                  )}
                </div>
                <span className="text-xl sm:text-2xl font-black text-white mt-0.5">
                  {formatCurrency(effectiveMonthlyPayment, account.currency)}
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5">
                  {isCustomStatementAmount ? 'Fijado según tu banco' : 'Calculado automáticamente'}
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-center sm:text-left">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate">
                  🏷️ Cuotas MSI
                </span>
                <span className="text-sm font-black text-slate-200 mt-0.5 block">
                  {formatCurrency(msiMonthPayment, account.currency)}
                </span>
                <span className="text-[10px] text-slate-400">
                  {activeMsiList.length} compra{activeMsiList.length === 1 ? '' : 's'} activa{activeMsiList.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-center sm:text-left">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate">
                  🛒 Compras Ciclo
                </span>
                <span className="text-sm font-black text-slate-200 mt-0.5 block">
                  {formatCurrency(effectiveCyclePurchases, account.currency)}
                </span>
                <span className="text-[10px] text-slate-400">
                  {isCustomStatementAmount ? 'Saldo restante del pago' : 'No diferidas'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Punto 8 y Punto 12: Control e Indicador Visual de Desglose de Tarjeta */}
        <div className="mb-6">
          {account.type === 'credit_card' ? (
            <div className="p-4 sm:p-5 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-700 space-y-3.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200/60 dark:border-indigo-800 flex items-center justify-center shrink-0">
                    <PieChart className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                      Progreso de Desglose de la Deuda
                      <span className={`text-[11px] font-black px-2 py-0.5 rounded-lg ${
                        isBreakdownExact
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : isBreakdownExceeded
                          ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                          : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300'
                      }`}>
                        {percentageBrokenDown}%
                      </span>
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Atribución de la deuda actual ({formatCurrency(totalDebt, account.currency)}) en movimientos individuales
                    </p>
                  </div>
                </div>

                {/* Botones de Acción Tarjeta */}
                <div className="grid grid-cols-2 gap-2.5 w-full sm:flex sm:items-stretch sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setIsPayCardModalOpen(true)}
                    className="flex-1 sm:flex-initial min-h-[48px] py-2.5 px-3.5 bg-emerald-600 hover:bg-emerald-700 active:scale-98 text-white font-bold rounded-xl text-xs sm:text-sm flex items-center justify-center text-center gap-1.5 shadow-md shadow-emerald-500/20 transition-all"
                  >
                    <DollarSign className="w-4 h-4 shrink-0" />
                    <span className="leading-tight">Pagar Tarjeta</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBreakdownModalInitialMode('normal');
                      setIsBreakdownModalOpen(true);
                    }}
                    className="flex-1 sm:flex-initial min-h-[48px] py-2.5 px-3.5 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 hover:from-indigo-700 hover:to-purple-800 active:scale-98 text-white font-bold rounded-xl text-xs sm:text-sm flex items-center justify-center text-center gap-1.5 shadow-md shadow-indigo-500/20 transition-all"
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    <span className="leading-tight">Movimiento / Desglose</span>
                  </button>
                </div>
              </div>

              {/* Barra de Progreso Visual */}
              <div className="w-full h-3 bg-slate-100 dark:bg-slate-700/60 rounded-full overflow-hidden p-0.5 border border-slate-200/60 dark:border-slate-600">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    isBreakdownExact
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                      : isBreakdownExceeded
                      ? 'bg-rose-500'
                      : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-amber-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.max(totalDesglosado > 0 ? 5 : 0, percentageBrokenDown))}%` }}
                />
              </div>

              {/* Badges y Métricas en Tiempo Real */}
              <div className="grid grid-cols-3 gap-2 pt-0.5">
                <div className="p-2 sm:p-2.5 rounded-xl bg-slate-50 dark:bg-slate-700/40 border border-slate-100 dark:border-slate-700 text-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate">
                    Deuda Tarjeta
                  </span>
                  <span className="text-xs sm:text-sm font-black text-slate-800 dark:text-slate-200">
                    {formatCurrency(totalDebt, account.currency)}
                  </span>
                </div>

                <div className="p-2 sm:p-2.5 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 text-center">
                  <span className="text-[10px] font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider block truncate">
                    Desglosado
                  </span>
                  <span className="text-xs sm:text-sm font-black text-indigo-700 dark:text-indigo-300">
                    {formatCurrency(totalDesglosado, account.currency)}
                  </span>
                </div>

                <div className={`p-2 sm:p-2.5 rounded-xl border text-center ${
                  isBreakdownExact
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800'
                    : isBreakdownExceeded
                    ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800'
                    : 'bg-amber-50/80 dark:bg-amber-950/30 border-amber-200/80 dark:border-amber-800'
                }`}>
                  <span className="text-[10px] font-bold uppercase tracking-wider block truncate text-slate-400">
                    {isBreakdownExact ? 'Estado' : isBreakdownExceeded ? 'Excedente' : 'Restante'}
                  </span>
                  <span className={`text-xs sm:text-sm font-black ${
                    isBreakdownExact
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : isBreakdownExceeded
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}>
                    {isBreakdownExact
                      ? '¡100% Cuadrado!'
                      : isBreakdownExceeded
                      ? `+${formatCurrency(excessBreakdown, account.currency)}`
                      : formatCurrency(remainingToBreakdown, account.currency)}
                  </span>
                </div>
              </div>

              {/* Mensajes de Estado */}
              {isBreakdownExceeded && (
                <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 flex items-center gap-2 text-xs text-rose-800 dark:text-rose-300">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>Los movimientos desglosados superan la deuda actual registrada por <strong>{formatCurrency(excessBreakdown, account.currency)}</strong>.</span>
                </div>
              )}

              {!isBreakdownExact && !isBreakdownExceeded && remainingToBreakdown > 0 && (
                <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300">
                  <span className="text-amber-600 font-bold shrink-0">ℹ️</span>
                  <span>Faltan <strong>{formatCurrency(remainingToBreakdown, account.currency)}</strong> por atribuir en movimientos desglosados.</span>
                </div>
              )}

              {isBreakdownExact && totalDebt > 0 && (
                <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2 text-xs text-emerald-800 dark:text-emerald-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="font-bold">¡Toda la deuda de la tarjeta está 100% atribuida y desglosada en movimientos!</span>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleOpenTransactionModal('expense', false, false)}
              className="w-full sm:w-auto py-3 px-6 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white font-bold rounded-2xl text-xs flex items-center justify-center gap-2 shadow-sm shadow-indigo-500/20 transition-all"
            >
              <Plus className="w-4 h-4" />
              Nuevo Movimiento en esta Cuenta
            </button>
          )}
        </div>

        {/* Transactions List & Phase 2 Tabs */}
        {(() => {
          const isCreditCard = account.type === 'credit_card';

          // 1. Filtrado base por pestaña (si es tarjeta)
          let baseList = [...transactions];
          const msiTransactions = transactions.filter(t => t.is_msi);
          const retentionTransactions = transactions.filter(t => t.is_retention);

          if (isCreditCard) {
            if (activeTab === 'msi') baseList = msiTransactions;
            else if (activeTab === 'retentions') baseList = retentionTransactions;
          }

          // 2. Filtrado por tipo (gasto o ingreso)
          if (filterType !== 'all') {
            baseList = baseList.filter(t => t.type === filterType);
          }

          // 3. Filtrado por texto de búsqueda
          if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            baseList = baseList.filter(t =>
              (t.description || '').toLowerCase().includes(q) ||
              (t.category || '').toLowerCase().includes(q) ||
              (t.notes || '').toLowerCase().includes(q)
            );
          }

          // 4. Ordenación
          baseList.sort((a, b) => {
            if (sortBy === 'amount-desc') {
              return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
            }
            if (sortBy === 'amount-asc') {
              return (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0);
            }
            if (sortBy === 'date-asc') {
              const dayA = (a.date || a.created_at || '').slice(0, 10);
              const dayB = (b.date || b.created_at || '').slice(0, 10);
              if (dayA !== dayB) return dayA.localeCompare(dayB);
              const createA = new Date(a.created_at || a.date || 0).getTime();
              const createB = new Date(b.created_at || b.date || 0).getTime();
              return createA - createB;
            }
            // Por defecto: 'date-desc' (más reciente primero)
            const dayA = (a.date || a.created_at || '').slice(0, 10);
            const dayB = (b.date || b.created_at || '').slice(0, 10);
            if (dayA !== dayB) return dayB.localeCompare(dayA);
            const createA = new Date(a.created_at || a.date || 0).getTime();
            const createB = new Date(b.created_at || b.date || 0).getTime();
            return createB - createA;
          });

          const displayTransactions = baseList;
          const activeFiltersCount = (filterType !== 'all' ? 1 : 0) + (searchQuery.trim() ? 1 : 0) + (sortBy !== 'date-desc' ? 1 : 0);

          return (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-700 overflow-visible relative">
              {/* Header con Pestañas si es Tarjeta de Crédito y Botonera */}
              <div className="p-4 border-b border-slate-100 dark:border-slate-700/80 space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {isCreditCard ? (
                    <div className="flex overflow-x-auto max-w-full no-scrollbar bg-slate-100 dark:bg-slate-700/80 p-1 rounded-xl border border-slate-200/80 dark:border-slate-600 self-start">
                      <button
                        type="button"
                        onClick={() => setActiveTab('all')}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          activeTab === 'all'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                        }`}
                      >
                        Todos ({transactions.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('msi')}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                          activeTab === 'msi'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                        }`}
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        MSI ({msiTransactions.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('retentions')}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                          activeTab === 'retentions'
                            ? 'bg-amber-600 text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                        }`}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        Retenciones ({retentionTransactions.length})
                      </button>
                    </div>
                  ) : (
                    <div>
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        Movimientos ({displayTransactions.length}{displayTransactions.length !== transactions.length ? ` de ${transactions.length}` : ''})
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Historial cronológico de esta cuenta
                      </p>
                    </div>
                  )}

                  {/* Botonera de Filtro y Descarga funcionales */}
                  <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowFilterModal(true)}
                      className={`relative p-2.5 rounded-xl border transition-all flex items-center justify-center shrink-0 active:scale-95 shadow-xs ${
                        activeFiltersCount > 0
                          ? 'bg-blue-50 border-blue-300 text-blue-600 dark:bg-blue-950/60 dark:border-blue-700 dark:text-blue-300 font-bold'
                          : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50'
                      }`}
                      title="Filtrar y buscar movimientos"
                      aria-label="Filtrar y buscar movimientos"
                    >
                      <Filter className="w-4 h-4" />
                      {activeFiltersCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-600 text-white rounded-full text-[10px] font-black flex items-center justify-center shadow-xs">
                          {activeFiltersCount}
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowExportModal(true)}
                      className="p-2.5 rounded-xl border bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 active:scale-95 shadow-xs transition-all"
                      title="Descargar movimientos en PDF, Excel o CSV"
                      aria-label="Descargar movimientos"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Píldoras de Filtros Activos */}
                {activeFiltersCount > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Activos:</span>
                    {searchQuery.trim() && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                        <span>"{searchQuery}"</span>
                        <button type="button" onClick={() => setSearchQuery('')} className="p-0.5 hover:bg-blue-200 rounded-full">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filterType !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                        <span>{filterType === 'expense' ? 'Solo Gastos' : 'Solo Ingresos'}</span>
                        <button type="button" onClick={() => setFilterType('all')} className="p-0.5 hover:bg-blue-200 rounded-full">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {sortBy !== 'date-desc' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                        <span>
                          {sortBy === 'date-asc' ? 'Más Antiguos' : sortBy === 'amount-desc' ? '$ Mayor a Menor' : '$ Menor a Mayor'}
                        </span>
                        <button type="button" onClick={() => setSortBy('date-desc')} className="p-0.5 hover:bg-blue-200 rounded-full">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setFilterType('all');
                        setSortBy('date-desc');
                      }}
                      className="text-[11px] font-bold text-slate-400 hover:text-slate-600 underline ml-1"
                    >
                      Limpiar todos
                    </button>
                  </div>
                )}
              </div>

              {/* Contenido de la lista */}
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {displayTransactions.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                      {activeTab === 'msi' 
                        ? 'No tienes compras a Meses Sin Intereses registradas'
                        : activeTab === 'retentions'
                          ? 'No tienes retenciones de saldo registradas'
                          : 'No hay movimientos'}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm max-w-sm mx-auto mb-4">
                      {activeTab === 'msi'
                        ? 'Calcula pagos mensuales diferidos y saldo pendiente para compras a 3, 6, 12, 18 meses o más.'
                        : activeTab === 'retentions'
                          ? 'Registra compras con retención o depósitos de garantía para que bloqueen tu disponible hasta que las liberes.'
                          : 'Los movimientos de esta cuenta aparecerán aquí'}
                    </p>
                    {isCreditCard ? (
                      activeTab === 'msi' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setBreakdownModalInitialMode('msi');
                            setIsBreakdownModalOpen(true);
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5"
                        >
                          <Plus className="w-4 h-4" />
                          + Registrar Compra a MSI
                        </button>
                      ) : activeTab === 'retentions' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setBreakdownModalInitialMode('retention');
                            setIsBreakdownModalOpen(true);
                          }}
                          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-md shadow-amber-500/20 inline-flex items-center gap-1.5"
                        >
                          <Lock className="w-4 h-4" />
                          + Registrar Retención de Saldo
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setBreakdownModalInitialMode('normal');
                            setIsBreakdownModalOpen(true);
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 inline-flex items-center gap-1.5"
                        >
                          <Plus className="w-4 h-4" />
                          + Desglosar Movimiento
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenTransactionModal('expense', false, false)}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 inline-flex items-center gap-1.5"
                      >
                        <Plus className="w-4 h-4" />
                        + Registrar Movimiento
                      </button>
                    )}
                  </div>
                ) : (
                  displayTransactions.map((transaction, index) => {
                    const isMsi = transaction.is_msi;
                    const isPaidShared = transaction.shared_status === 'paid';
                    const msiMonths = transaction.msi_months || 12;
                    const monthsPaid = isPaidShared ? msiMonths : (transaction.msi_months_paid || 0);
                    const monthsRemaining = isPaidShared ? 0 : Math.max(0, msiMonths - monthsPaid);
                    const monthlyPayment = transaction.msi_monthly_payment || (transaction.amount / msiMonths);
                    const remainingBalance = isPaidShared ? 0 : Math.max(0, transaction.amount - (monthsPaid * monthlyPayment));
                    const isRetention = transaction.is_retention;
                    const isReleased = transaction.retention_status === 'released';
                    const hasItems = transaction.items_breakdown?.length > 0;
                    const isExpanded = expandedBreakdownId === transaction.id;

                    return (
                      <div
                        key={transaction.id}
                        className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        {/* Fila Principal: Icono + Título/Badges + Monto/Menú */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isRetention
                                ? 'bg-slate-100 dark:bg-slate-700'
                                : isMsi
                                ? 'bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800'
                                : transaction.is_breakdown
                                ? 'bg-purple-100 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800'
                                : 'bg-slate-100 dark:bg-slate-700'
                            }`}>
                              {isRetention ? (
                                isReleased ? <Unlock className="w-5 h-5 text-emerald-600" /> : <Lock className="w-5 h-5 text-amber-600" />
                              ) : isMsi ? (
                                <CreditCard className="w-5 h-5 text-blue-600" />
                              ) : transaction.is_breakdown ? (
                                <PieChart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              ) : (
                                getTransactionIcon(transaction.type)
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-slate-900 dark:text-white truncate">
                                  {transaction.description || 'Sin descripción'}
                                </p>

                                {/* Badges informativos de Phase 2 */}
                                {transaction.is_breakdown && (
                                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] font-bold rounded-full">
                                    Desglose de Saldo
                                  </span>
                                )}

                                {isMsi && (
                                  <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded-full">
                                    MSI: {monthsPaid}/{msiMonths} meses
                                  </span>
                                )}

                                {isRetention && (
                                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                    isReleased
                                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                                      : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 animate-pulse'
                                  }`}>
                                    {isReleased ? 'Retención Liberada' : 'Retención Activa'}
                                  </span>
                                )}

                                {hasItems && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedBreakdownId(isExpanded ? null : transaction.id)}
                                    className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-bold rounded-full flex items-center gap-1 hover:bg-indigo-100 transition-colors"
                                  >
                                    <Package className="w-3 h-3" />
                                    {transaction.items_breakdown.length} artículos
                                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </button>
                                )}
                              </div>

                              {(() => {
                                const bal = transactionBalancesMap[transaction.id] || 
                                            (transaction.previous_balance !== null && transaction.previous_balance !== undefined && transaction.new_balance !== null && transaction.new_balance !== undefined
                                              ? { prev: transaction.previous_balance, next: transaction.new_balance }
                                              : getAdjustmentDetails(transaction));
                                if (!bal) return null;
                                const prevVal = bal.prev;
                                const nextVal = bal.next ?? bal.curr;
                                const isExpense = transaction.type === 'expense';
                                const nextColor = isExpense ? 'text-rose-500/90 dark:text-rose-400/90' : 'text-emerald-600/90 dark:text-emerald-400/90';
                                return (
                                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500 bg-slate-50/70 dark:bg-slate-800/40 px-2 py-0.5 rounded-lg w-fit border border-slate-100 dark:border-slate-700/50">
                                    <span className="font-normal opacity-80">Ant:</span>
                                    <span className="font-medium text-slate-600 dark:text-slate-400">
                                      {prevVal !== undefined && !isNaN(prevVal)
                                        ? formatCurrency(isCreditCard ? Math.abs(prevVal) : prevVal, account.currency)
                                        : 'N/D'}
                                    </span>
                                    <span className="text-slate-300 dark:text-slate-600 font-normal">→</span>
                                    <span className="font-normal opacity-80">Después:</span>
                                    <span className={`font-semibold ${nextColor}`}>
                                      {nextVal !== undefined && !isNaN(nextVal)
                                        ? formatCurrency(isCreditCard ? Math.abs(nextVal) : nextVal, account.currency)
                                        : (bal.notes || 'N/D')}
                                    </span>
                                  </div>
                                );
                              })()}

                              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 mt-1">
                                <span>{formatDate(transaction.date)}</span>
                                {transaction.category && (
                                  <>
                                    <span>•</span>
                                    <span>{transaction.category}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            <div className="text-right">
                              <p className={`font-bold text-sm sm:text-base ${
                                isRetention && isReleased
                                  ? 'text-slate-400 line-through'
                                  : isMsi
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : transaction.is_breakdown
                                  ? 'text-purple-600 dark:text-purple-400'
                                  : getTransactionColor(transaction.type)
                              }`}>
                                {transaction.type === 'expense' ? '-' : ''}
                                {formatCurrency(transaction.amount, account.currency)}
                              </p>
                            </div>

                            {/* Botón Opciones del Movimiento */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.currentTarget?.blur();
                                setSelectedTxForActions(transaction);
                              }}
                              className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all active:scale-95 shrink-0"
                              title="Opciones del movimiento"
                              aria-label="Opciones del movimiento"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Fila Secundaria a Ancho Completo: MSI, Retenciones, Gasto Compartido y Artículos */}
                        {(isMsi || (isRetention && !isReleased) || transaction.shared_with_name || (hasItems && isExpanded)) && (
                          <div className="mt-3 sm:pl-[3.25rem] space-y-2.5 w-full">
                            {/* Barra de progreso y cálculo MSI */}
                            {isMsi && (
                              <div className="p-2.5 bg-blue-50/70 dark:bg-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/40 w-full max-w-full">
                                <div className="flex flex-wrap justify-between gap-1 text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">
                                  <span>Cuota mensual: <strong>${monthlyPayment.toFixed(2)}</strong></span>
                                  <span>Restante: <strong>${remainingBalance.toFixed(2)}</strong> ({monthsRemaining} meses)</span>
                                </div>
                                <div className="w-full h-2 bg-blue-200 dark:bg-blue-900 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-500 ${isPaidShared ? 'bg-emerald-500' : 'bg-blue-600'}`} 
                                    style={{ width: `${isPaidShared ? 100 : Math.min(100, (monthsPaid / msiMonths) * 100)}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Acciones de Retención: Liberar o Pasar a Cobro */}
                            {isRetention && !isReleased && (
                              <div className="flex flex-wrap items-center gap-2 w-full">
                                <button
                                  type="button"
                                  disabled={releasingRetentionId === transaction.id}
                                  onClick={() => handleReleaseRetention(transaction.id)}
                                  className="flex-1 sm:flex-none justify-center px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                                >
                                  <Unlock className="w-3.5 h-3.5" />
                                  {releasingRetentionId === transaction.id ? 'Liberando...' : 'Liberar Saldo'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleConvertToCharge(transaction.id)}
                                  className="flex-1 sm:flex-none justify-center px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
                                >
                                  <CreditCard className="w-3.5 h-3.5" />
                                  Pasar a Cobro Definitivo
                                </button>
                                {transaction.retention_expected_release_date && (
                                  <span className="w-full sm:w-auto text-[11px] font-bold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-900/50 px-2.5 py-1.5 rounded-xl flex items-center justify-center sm:justify-start gap-1.5 shrink-0">
                                    <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                    <span>Vence: {formatDate(transaction.retention_expected_release_date)}</span>
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Gasto Compartido ("Pagué por alguien más") */}
                            {transaction.shared_with_name && (
                              <div className="p-3 bg-emerald-50/80 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs w-full max-w-full">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <Share2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                  <span className="text-slate-700 dark:text-slate-300 break-words">
                                    Pagué por <strong className="text-slate-900 dark:text-white font-bold">{transaction.shared_with_name}</strong>: ${transaction.shared_amount?.toFixed(2)}
                                  </span>
                                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full shrink-0 ${
                                    transaction.shared_status === 'paid'
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                  }`}>
                                    {transaction.shared_status === 'paid' ? 'Liquidado ✓' : 'Por cobrar'}
                                  </span>
                                </div>
                                  <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/debts?tab=they_owe_me${transaction.debt_id ? `&debtId=${transaction.debt_id}` : ''}`)}
                                      className="flex-1 sm:flex-none justify-center px-2.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold transition-all flex items-center gap-1 shadow-sm active:scale-95"
                                    >
                                      <ArrowRightLeft className="w-3 h-3" />
                                      <span>Ver en Me Deben</span>
                                    </button>
                                    {transaction.shared_payment_token && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const url = `${window.location.origin}/public/debt/${transaction.shared_payment_token}`;
                                          navigator.clipboard.writeText(url);
                                          showToast.success('Liga de cobro copiada al portapapeles');
                                        }}
                                        className="flex-1 sm:flex-none justify-center px-2.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 shadow-sm"
                                      >
                                        <Copy className="w-3 h-3" />
                                        <span>Copiar Liga</span>
                                      </button>
                                    )}
                                    {transaction.shared_status !== 'paid' ? (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            await axios.post(`${BACKEND_URL}/api/transactions/${transaction.id}/confirm-shared-payment`, {}, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            showToast.success('Pago marcado como liquidado');
                                            await fetchAccountDetails();
                                            await fetchAccountTransactions();
                                          } catch {
                                            showToast.error('Error confirmando pago');
                                          }
                                        }}
                                        className="flex-1 sm:flex-none justify-center px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm"
                                      >
                                        Marcar Pagado
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            await axios.post(`${BACKEND_URL}/api/transactions/${transaction.id}/reopen-shared-payment`, {}, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            showToast.success('Cobro reabierto correctamente');
                                            await fetchAccountDetails();
                                            await fetchAccountTransactions();
                                          } catch {
                                            showToast.error('Error reabriendo cobro');
                                          }
                                        }}
                                        className="flex-1 sm:flex-none justify-center px-2.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold shadow-sm"
                                      >
                                        Reabrir Cobro
                                      </button>
                                    )}
                                  </div>
                              </div>
                            )}

                            {/* Desglose de artículos desplegado */}
                            {hasItems && isExpanded && (
                              <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700 w-full max-w-full space-y-1.5">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                  Artículos de esta compra
                                </p>
                                {transaction.items_breakdown.map((it, i) => (
                                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                                    <span className="text-slate-700 dark:text-slate-300 truncate mr-2">{it.name}</span>
                                    <span className="font-bold text-slate-900 dark:text-white shrink-0">${parseFloat(it.price).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && transactionToDelete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              ¿Eliminar transacción?
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              ¿Estás seguro de que deseas eliminar la transacción "{transactionToDelete.description || 'Sin descripción'}"?
              Esta acción no se puede deshacer y el saldo de la cuenta se ajustará automáticamente.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setTransactionToDelete(null);
                }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteTransaction(transactionToDelete.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Balance Modal Responsivo con Soporte Rendimiento Real (Punto 14) */}
      {showAdjustBalance && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm overflow-hidden w-full max-w-full overscroll-contain"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAdjustBalance(false);
              setNewBalance('');
              setAdjustReason('');
              setAsRealYield(false);
            }
          }}
        >
          <div 
            style={{
              transform: adjustDragCurrentY > 0 ? `translate3d(0, ${adjustDragCurrentY}px, 0)` : undefined,
              transition: isAdjustDragging.current ? 'none' : 'transform 0.2s ease-out'
            }}
            className="bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-2xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-700 animate-in slide-in-from-bottom-5 duration-200 will-change-transform"
          >
            {/* Handle táctil móvil */}
            <div 
              style={{ touchAction: 'none' }}
              onTouchStart={(e) => handleAdjustTouchStart(e, true)}
              onTouchMove={handleAdjustTouchMove}
              onTouchEnd={handleAdjustTouchEnd}
              className="sm:hidden pt-3 pb-1.5 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none bg-white dark:bg-slate-800 -mt-2 mb-2"
            >
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
            </div>

            <div 
              onTouchStart={(e) => handleAdjustTouchStart(e, false)}
              onTouchMove={handleAdjustTouchMove}
              onTouchEnd={handleAdjustTouchEnd}
              className="flex items-center justify-between"
            >
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">Ajustar Saldo</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAdjustBalance(false);
                  setNewBalance('');
                  setAdjustReason('');
                  setAsRealYield(false);
                }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
              Ingresa el saldo real en banco de esta cuenta. Se actualizará el saldo base automáticamente.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  Nuevo Saldo Real
                </label>
                <input
                  type="text"
                  value={newBalance ? formatCurrencyInput(newBalance) : ''}
                  onChange={(e) => {
                    const rawValue = parseCurrency(e.target.value);
                    setNewBalance(rawValue);
                  }}
                  placeholder="$0.00"
                  className="w-full px-4 py-2.5 text-base sm:text-lg font-bold border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700/70 text-slate-900 dark:text-white shadow-sm"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                  Saldo base (sin rendimiento estimado): <span className="font-semibold">{formatCurrency((account?.current_balance || 0) - (account?.has_yield ? (account?.accrued_yield || 0) : 0), account?.currency)}</span>
                  {account?.has_yield && (account?.accrued_yield || 0) > 0 && (
                    <span className="ml-1.5 text-amber-600 dark:text-amber-400">(+{formatCurrency(account.accrued_yield, account?.currency)} estimado, no incluido)</span>
                  )}
                </p>
              </div>

              {/* Checkbox inteligente para cuentas con rendimiento (Punto 14) */}
              {account?.has_yield && (() => {
                const targetVal = parseFloat(newBalance) || 0;
                // Usar saldo base (sin accrued_yield) para calcular la diferencia real
                const currVal = (account?.current_balance || 0) - (account?.accrued_yield || 0);
                const diff = targetVal > 0 ? (targetVal - currVal) : 0;

                return (
                  <div className="p-3.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/80 rounded-2xl space-y-2">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={asRealYield}
                        onChange={(e) => setAsRealYield(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer shrink-0"
                      />
                      <span className="text-xs sm:text-sm font-bold text-amber-900 dark:text-amber-200 leading-snug">
                        ¿Registrar {targetVal > 0 && diff !== 0 ? `la diferencia (${diff > 0 ? '+' : ''}${formatCurrency(diff, account?.currency)})` : 'el ajuste'} como rendimiento generado por el banco?
                      </span>
                    </label>
                    <p className="text-[11px] text-amber-800 dark:text-amber-300/80 pl-6.5 leading-relaxed">
                      {asRealYield
                        ? '⚡ Se creará una transacción en movimientos con categoría "Rendimientos", saldo anterior y saldo después, actualizando tu tasa y rendimiento real depositado.'
                        : 'Se creará una transacción de ajuste regular en tu historial de movimientos.'}
                    </p>
                  </div>
                );
              })()}

              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  Motivo (opcional)
                </label>
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder={account?.has_yield && asRealYield ? "Rendimiento" : "Ajuste de saldo"}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700/70 text-slate-900 dark:text-white text-xs sm:text-sm shadow-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAdjustBalance(false);
                  setNewBalance('');
                  setAdjustReason('');
                  setAsRealYield(Boolean(account?.has_yield));
                }}
                className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs sm:text-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSubmittingAdjust || !newBalance}
                onClick={handleAdjustBalance}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-98 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <span>{isSubmittingAdjust ? 'Ajustando...' : 'Ajustar Saldo'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Rendimiento Real: Bottom-Sheet Responsivo (Punto 13) */}
      {showRealYieldModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm overflow-hidden w-full max-w-full overscroll-contain"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRealYieldModal(false);
              setRealYieldResult(null);
            }
          }}
        >
          <div 
            style={{
              transform: yieldDragCurrentY > 0 ? `translate3d(0, ${yieldDragCurrentY}px, 0)` : undefined,
              transition: isYieldDragging.current ? 'none' : 'transform 0.2s ease-out'
            }}
            className="bg-white dark:bg-slate-800 rounded-t-[2.2rem] sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl relative max-h-[85vh] sm:max-h-[90vh] overflow-y-auto flex flex-col box-border animate-in slide-in-from-bottom-5 duration-200 will-change-transform"
          >
            {/* Handle táctil en móvil */}
            <div 
              style={{ touchAction: 'none' }}
              onTouchStart={(e) => handleYieldTouchStart(e, true)}
              onTouchMove={handleYieldTouchMove}
              onTouchEnd={handleYieldTouchEnd}
              className="w-full pt-1 pb-3 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none sm:hidden -mt-1"
            >
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
            </div>

            <button
              onClick={() => {
                setShowRealYieldModal(false);
                setRealYieldResult(null);
              }}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div 
              onTouchStart={(e) => handleYieldTouchStart(e, false)}
              onTouchMove={handleYieldTouchMove}
              onTouchEnd={handleYieldTouchEnd}
              className="flex items-center gap-2 text-amber-500 mb-2 select-none"
            >
              <Zap className="w-5 h-5 fill-amber-500 shrink-0" />
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">Rendimiento Real Recibido</h3>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
              Registra lo que <strong>realmente te depositó el banco</strong>. Hace un <strong>ajuste silencioso</strong> a tu saldo sin crear movimientos que ensucien tu historial y <strong>actualiza la tasa anual de la cuenta</strong> con el porcentaje verdadero obtenido.
            </p>

            {!realYieldResult ? (
              <form onSubmit={handleRecordRealYield} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Monto Real Depositado *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    value={realYieldAmount ? formatCurrencyInput(realYieldAmount) : ''}
                    onChange={(e) => setRealYieldAmount(parseCurrency(e.target.value))}
                    placeholder="Ej: $450.20"
                    className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-bold text-lg"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Saldo base actual: {formatCurrency(account?.current_balance || 0, account?.currency)}
                  </p>
                </div>

                <div className="w-full max-w-full">
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-amber-500" />
                    <span>Fecha del Depósito *</span>
                  </label>
                  <div className="w-full max-w-full overflow-hidden rounded-xl">
                    <input
                      type="date"
                      required
                      value={realYieldDate}
                      onChange={(e) => setRealYieldDate(e.target.value)}
                      className="w-full max-w-full box-border px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-xs sm:text-sm font-semibold text-left [&::-webkit-date-and-time-value]:text-left"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Desde la fecha de inicio actual ({account?.yield_start_date ? formatDate(account.yield_start_date) : 'inicial'})
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRealYieldModal(false)}
                    className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs sm:text-sm hover:bg-slate-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingYield}
                    className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 active:scale-98 text-slate-950 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    <Zap className="w-4 h-4 fill-slate-950 shrink-0" />
                    <span>{isSubmittingYield ? 'Registrando...' : 'Registrar'}</span>
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl space-y-3">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <span className="font-bold text-sm">¡Rendimiento registrado con éxito!</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-emerald-200/60 dark:border-emerald-800/60">
                    <div>
                      <p className="text-slate-500">Monto depositado:</p>
                      <p className="font-black text-slate-900 dark:text-white text-sm">
                        +{formatCurrency(realYieldResult.real_yield_registered, account.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Días calculados:</p>
                      <p className="font-black text-slate-900 dark:text-white text-sm">
                        {realYieldResult.days_elapsed} días
                      </p>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/60">
                      <p className="text-slate-500">Rendimiento Real Obtenido (Nueva Tasa Anual Efectiva):</p>
                      <p className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                        {realYieldResult.effective_annual_rate}% anual
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Tasa anterior: {realYieldResult.promised_rate}% anual
                      </p>
                      <div className="mt-2 p-2 bg-emerald-100/70 dark:bg-emerald-900/40 rounded-xl text-[11px] font-bold text-emerald-900 dark:text-emerald-200">
                        ✓ Tasa de tu cuenta actualizada a {realYieldResult.effective_annual_rate}% para los próximos cálculos diarios.
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowRealYieldModal(false);
                    setRealYieldResult(null);
                  }}
                  className="w-full py-2.5 bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 active:scale-98 transition-all"
                >
                  Entendido
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal de Transacción directo en la Cuenta */}
      <TransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => {
          setIsTransactionModalOpen(false);
          setEditingTransaction(null);
        }}
        onSuccess={() => {
          fetchAccountDetails();
          fetchAccountTransactions();
        }}
        preselectedType={modalPreselectedType}
        preselectedAccountId={account?.id}
        initialMsi={modalInitialMsi}
        initialRetention={modalInitialRetention}
        editingTransaction={editingTransaction}
      />

      {/* Modal Exclusivo de Desglose de Tarjeta */}
      <CreditCardBreakdownModal
        isOpen={isBreakdownModalOpen}
        onClose={() => {
          setIsBreakdownModalOpen(false);
          setEditingTransaction(null);
        }}
        onSuccess={() => {
          fetchAccountDetails();
          fetchAccountTransactions();
          setEditingTransaction(null);
        }}
        account={account}
        initialMode={breakdownModalInitialMode}
        initialEntryType="expense"
        editingTransaction={editingTransaction}
      />

      {/* Modal Pagar Tarjeta de Crédito Inteligente */}
      {isPayCardModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-hidden w-full max-w-full overscroll-contain"
          onClick={(e) => { if (e.target === e.currentTarget) setIsPayCardModalOpen(false); }}
        >
          <div 
            style={{
              transform: payDragCurrentY > 0 ? `translate3d(0, ${payDragCurrentY}px, 0)` : undefined,
              transition: isPayDragging.current ? 'none' : 'transform 0.2s ease-out'
            }}
            className="w-full max-w-full sm:max-w-lg bg-white dark:bg-slate-900 rounded-t-[2.2rem] sm:rounded-3xl shadow-2xl border border-slate-200/80 dark:border-slate-800 max-h-[92vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-200 will-change-transform"
          >
            {/* Drag Bar (Mobile) */}
            <div
              style={{ touchAction: 'none' }}
              onTouchStart={(e) => handlePayTouchStart(e, true)}
              onTouchMove={handlePayTouchMove}
              onTouchEnd={handlePayTouchEnd}
              className="sm:hidden pt-3 pb-1.5 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none bg-white dark:bg-slate-900"
            >
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
            </div>

            {/* Header */}
            <div 
              onTouchStart={(e) => handlePayTouchStart(e, false)}
              onTouchMove={handlePayTouchMove}
              onTouchEnd={handlePayTouchEnd}
              className="px-5 sm:px-6 py-3 sm:py-3.5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-black text-slate-900 dark:text-white">
                    Pagar Tarjeta de Crédito
                  </h2>
                  <p className="text-[11px] sm:text-xs text-slate-500">
                    {account?.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPayCardModalOpen(false)}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Formulario */}
            <form 
              ref={payScrollRef}
              onSubmit={handlePayCreditCard} 
              className="p-4 sm:p-5 overflow-y-auto space-y-3 sm:space-y-3.5 flex-1 overscroll-contain"
            >
              {/* Resumen del Pago Sugerido */}
              <div className="p-3 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-800/80 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-emerald-900 dark:text-emerald-200">
                    Pago sugerido para no generar intereses:
                  </span>
                  <span className="text-xs sm:text-sm font-black text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(effectiveMonthlyPayment, account.currency)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-emerald-200/60 dark:border-emerald-800/60 text-[11px] text-slate-600 dark:text-slate-400">
                  <div>
                    <span>Compras ciclo:</span>{' '}
                    <strong className="text-slate-900 dark:text-white">{formatCurrency(effectiveCyclePurchases, account.currency)}</strong>
                  </div>
                  <div>
                    <span>Cuotas MSI del mes:</span>{' '}
                    <strong className="text-slate-900 dark:text-white">{formatCurrency(msiMonthPayment, account.currency)}</strong>
                  </div>
                </div>
                {isCustomStatementAmount && (effectiveMonthlyPayment - suggestedMonthlyPayment) !== 0 && (
                  <div className="flex items-center justify-between text-[11px] text-amber-700 dark:text-amber-300 pt-0.5 border-t border-emerald-200/60 dark:border-emerald-800/60">
                    <span>Ajuste Estado de Cuenta:</span>
                    <strong className="font-bold">
                      {(effectiveMonthlyPayment - suggestedMonthlyPayment) > 0
                        ? `+${formatCurrency(effectiveMonthlyPayment - suggestedMonthlyPayment, account.currency)}`
                        : `-${formatCurrency(Math.abs(effectiveMonthlyPayment - suggestedMonthlyPayment), account.currency)}`}
                    </strong>
                  </div>
                )}
                {activeRetentionsNotDueDebt > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-amber-700 dark:text-amber-300 pt-0.5 border-t border-emerald-200/60 dark:border-emerald-800/60">
                    <span>Retenciones temporales no vencidas:</span>
                    <strong className="font-bold">{formatCurrency(activeRetentionsNotDueDebt, account.currency)} (No exigibles este mes)</strong>
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-0.5">
                  <span>Deuda total acumulada:</span>
                  <strong className="text-slate-700 dark:text-slate-300">{formatCurrency(totalDebt, account.currency)}</strong>
                </div>
              </div>

              {/* Botones de Selección Rápida de Monto */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  ¿Cuánto deseas pagar?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPayAmount(effectiveMonthlyPayment.toFixed(2))}
                    className={`p-2 rounded-xl border text-xs font-bold transition-all text-left ${
                      parseFloat(payAmount) === parseFloat(effectiveMonthlyPayment.toFixed(2))
                        ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-500 text-emerald-700 dark:text-emerald-300'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="block text-[10px] text-slate-400">Pago del Mes</span>
                    {formatCurrency(effectiveMonthlyPayment, account.currency)}
                  </button>

                  <button
                    type="button"
                    onClick={() => setPayAmount(totalDebt.toFixed(2))}
                    className={`p-2 rounded-xl border text-xs font-bold transition-all text-left ${
                      parseFloat(payAmount) === parseFloat(totalDebt.toFixed(2))
                        ? 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-500 text-indigo-700 dark:text-indigo-300'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="block text-[10px] text-slate-400">Liquidar Total</span>
                    {formatCurrency(totalDebt, account.currency)}
                  </button>
                </div>
              </div>

              {/* Input Monto Personalizado */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Monto a Pagar *
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder="0.00"
                    value={payAmount ? formatCurrencyInput(payAmount) : ''}
                    onChange={(e) => setPayAmount(parseCurrency(e.target.value))}
                    className="w-full pl-8 pr-3.5 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-sm sm:text-base font-black text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Método de Pago */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block">
                  Origen del Dinero
                </label>
                <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setPaySourceType('transfer')}
                    className={`py-1.5 px-2 rounded-lg text-xs font-bold transition-all ${
                      paySourceType === 'transfer'
                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm'
                        : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    Desde mi cuenta
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaySourceType('direct')}
                    className={`py-1.5 px-2 rounded-lg text-xs font-bold transition-all ${
                      paySourceType === 'direct'
                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm'
                        : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    Efectivo / Externo
                  </button>
                </div>

                {paySourceType === 'transfer' && (
                  <div>
                    {bankAccountsList.length === 0 ? (
                      <p className="text-xs text-amber-600 p-2 bg-amber-50 rounded-xl">No tienes cuentas bancarias registradas. Usa Efectivo / Externo.</p>
                    ) : (
                      <select
                        value={paySourceAccountId}
                        onChange={(e) => setPaySourceAccountId(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white mt-1"
                      >
                        {bankAccountsList.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name} ({formatCurrency(acc.current_balance, acc.currency)})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>

              {/* Checkbox Avanzar MSI si hay cuotas activas */}
              {activeMsiList.length > 0 && (
                <div className="p-2.5 bg-indigo-50/60 dark:bg-indigo-950/30 rounded-xl border border-indigo-100 dark:border-indigo-900/40">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={payAdvanceMsi}
                      onChange={(e) => setPayAdvanceMsi(e.target.checked)}
                      className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="text-xs">
                      <span className="font-bold text-indigo-950 dark:text-indigo-200 block">
                        Avanzar mensualidad a {activeMsiList.length} compra(s) a MSI
                      </span>
                      <span className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 block">
                        Suma +1 a los meses pagados de tus compras a plazos activas
                      </span>
                    </div>
                  </label>
                </div>
              )}

              {/* Botones Acciones */}
              <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsPayCardModalOpen(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingPay}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-98 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 transition-all disabled:opacity-50"
                >
                  {isSubmittingPay ? 'Aplicando...' : 'Confirmar Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Ajustar Estado de Cuenta Mensual / Fechas & Recordatorio */}
      {showStatementModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-hidden overscroll-contain"
          onClick={(e) => { if (e.target === e.currentTarget) setShowStatementModal(false); }}
        >
          <div
            style={{
              transform: statementDragCurrentY > 0 ? `translate3d(0, ${statementDragCurrentY}px, 0)` : undefined,
              transition: isStatementDragging.current ? 'none' : 'transform 0.2s ease-out'
            }}
            className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-5 duration-200 overflow-hidden will-change-transform"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle táctil móvil */}
            <div
              style={{ touchAction: 'none' }}
              onTouchStart={(e) => handleStatementTouchStart(e, true)}
              onTouchMove={handleStatementTouchMove}
              onTouchEnd={handleStatementTouchEnd}
              className="sm:hidden pt-3 pb-1.5 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none bg-white dark:bg-slate-900"
            >
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto" />
            </div>

            {/* Header */}
            <div
              onTouchStart={(e) => handleStatementTouchStart(e, false)}
              onTouchMove={handleStatementTouchMove}
              onTouchEnd={handleStatementTouchEnd}
              className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-black text-slate-900 dark:text-white">
                    Ajustar Pago del Mes y Fechas
                  </h2>
                  <p className="text-[11px] sm:text-xs text-slate-500">
                    {account?.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowStatementModal(false)}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Contenido del Formulario */}
            <form 
              ref={statementScrollRef}
              onSubmit={handleSaveStatement} 
              className="p-4 sm:p-5 overflow-y-auto space-y-4 flex-1 overscroll-contain"
            >
              {/* Fechas de Corte y Límite de Pago */}
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Día de Corte
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="Ej. 15"
                    value={statementCutDay}
                    onChange={(e) => setStatementCutDay(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-semibold text-slate-900 dark:text-white"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Día del mes (1-31)</span>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Día Límite de Pago *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    required
                    placeholder="Ej. 5"
                    value={statementDueDay}
                    onChange={(e) => setStatementDueDay(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-semibold text-slate-900 dark:text-white"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Fecha límite sin intereses</span>
                </div>
              </div>

              {/* Desfase de Vencimiento de Pago */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1.5">
                  Mes de Vencimiento de Pago
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setStatementOffsetMonths(0)}
                    className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                      Number(statementOffsetMonths) === 0
                        ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    Mismo mes
                    <span className="block text-[9px] font-normal opacity-80">(Predeterminado)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatementOffsetMonths(1)}
                    className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                      Number(statementOffsetMonths) === 1
                        ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    +1 mes
                    <span className="block text-[9px] font-normal opacity-80">(Mes siguiente)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatementOffsetMonths(2)}
                    className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                      Number(statementOffsetMonths) === 2
                        ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    +2 meses
                    <span className="block text-[9px] font-normal opacity-80">(2 meses después)</span>
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  {Number(statementOffsetMonths) === 0 && 'La fecha límite vence en el mismo mes calendario que la fecha de corte.'}
                  {Number(statementOffsetMonths) === 1 && 'La fecha límite vence 1 mes después del corte (ej. corte en oct, pago en nov).'}
                  {Number(statementOffsetMonths) === 2 && 'La fecha límite vence 2 meses después del corte.'}
                </p>
              </div>

              {/* Monto a pagar del Estado de Cuenta */}
              <div className="space-y-2 p-3.5 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-indigo-950 dark:text-indigo-200 block">
                    Monto a pagar del Estado de Cuenta
                  </label>
                  {suggestedMonthlyPayment > 0 && (
                    <button
                      type="button"
                      onClick={() => setStatementMonthlyAmount(formatCurrencyInput(String(suggestedMonthlyPayment.toFixed(2))))}
                      className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Sugerido: {formatCurrency(suggestedMonthlyPayment, account.currency)}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={formatCurrencyInput(String(suggestedMonthlyPayment.toFixed(2)))}
                    value={statementMonthlyAmount}
                    onChange={(e) => setStatementMonthlyAmount(formatCurrencyInput(e.target.value))}
                    className="w-full pl-7 pr-3 py-2.5 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800/60 rounded-xl text-sm font-black text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <p className="text-[10.5px] text-slate-500 dark:text-slate-400 leading-relaxed">
                  Ingresa lo que te pide tu banco en el estado de cuenta. El sistema cubrirá primero las cuotas MSI y el resto se asignará a tus compras del ciclo.
                </p>
              </div>

              {/* Cálculo en Vivo */}
              {(() => {
                const liveTotal = statementMonthlyAmount ? parseCurrency(statementMonthlyAmount) : (suggestedMonthlyPayment || 0);
                const liveMsi = Math.min(liveTotal, msiMonthPayment);
                const livePurchases = Math.max(0, liveTotal - msiMonthPayment);
                const liveDiff = liveTotal - suggestedMonthlyPayment;

                return (
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700 space-y-2 text-xs">
                    <div className="flex justify-between text-slate-600 dark:text-slate-400">
                      <span>Total fijado a pagar:</span>
                      <span className="font-bold text-slate-900 dark:text-white">{formatCurrency(liveTotal, account.currency)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600 dark:text-slate-400">
                      <span>↳ Cuotas MSI a cubrir:</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">-{formatCurrency(liveMsi, account.currency)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600 dark:text-slate-400">
                      <span>↳ Resto compras del ciclo:</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(livePurchases, account.currency)}</span>
                    </div>
                    {liveDiff !== 0 && (
                      <div className="pt-1.5 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center text-[11px]">
                        <span className="text-slate-500">
                          {liveDiff > 0 ? 'Diferencia no desglosada / anualidad:' : 'Diferencia vs movimientos cargados:'}
                        </span>
                        <span className={`font-bold ${liveDiff > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {liveDiff > 0 ? `+${formatCurrency(liveDiff, account.currency)}` : `-${formatCurrency(Math.abs(liveDiff), account.currency)}`}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Configuración de Recordatorio Automático */}
              <div className="space-y-2 p-3 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statementEnableReminder}
                    onChange={(e) => setStatementEnableReminder(e.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="text-xs">
                    <span className="font-bold text-emerald-950 dark:text-emerald-200 block">
                      Recordatorio automático antes de la fecha límite
                    </span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 block">
                      Te notificará por email, push y campanita
                    </span>
                  </div>
                </label>

                {statementEnableReminder && (
                  <div className="pt-1.5 flex items-center justify-between text-xs">
                    <span className="text-slate-600 dark:text-slate-400">Avisarme con anticipación:</span>
                    <div className="flex gap-1.5">
                      {[1, 3, 5].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setStatementReminderDays(days)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                            statementReminderDays === days
                              ? 'bg-emerald-600 text-white shadow-sm'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                          }`}
                        >
                          {days} día{days > 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Botones de Acción */}
              <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowStatementModal(false)}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingStatement}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 transition-all disabled:opacity-50"
                >
                  {isSubmittingStatement ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL BOTTOM-SHEET: ACCIONES DEL MOVIMIENTO ─── */}
      <MobileBottomSheet
        isOpen={!!selectedTxForActions}
        onClose={() => setSelectedTxForActions(null)}
      >
        {({ close }) => {
          if (!selectedTxForActions) return null;
          const tx = selectedTxForActions;
          const isExpense = tx.type === 'expense';
          const isIncome = tx.type === 'income';
          const isCC = account?.type === 'credit_card';
          const isMsi = tx.is_msi;
          const isRetention = tx.is_retention;
          const isReleased = tx.retention_status === 'released';
          const txBal = transactionBalancesMap[tx.id] || getAdjustmentDetails(tx);

          return (
            <div className="space-y-3.5">
              {/* Cabecera del movimiento */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-xs ${
                    isExpense ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400' :
                    isIncome ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400' :
                    'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400'
                  }`}>
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-base text-slate-900 dark:text-white truncate">
                      {tx.description || 'Movimiento sin descripción'}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {formatDate(tx.date)} {tx.category ? `· ${tx.category}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Resumen Destacado de Monto y Saldos */}
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-2xl flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Importe</span>
                  <span className={`text-xl font-black ${
                    isRetention && isReleased ? 'text-slate-400 line-through' :
                    isExpense ? 'text-rose-600 dark:text-rose-400' :
                    isIncome ? 'text-emerald-600 dark:text-emerald-400' :
                    'text-blue-600 dark:text-blue-400'
                  }`}>
                    {isExpense ? '-' : isIncome ? '+' : ''}{formatCurrency(tx.amount, account?.currency)}
                  </span>
                </div>
                {txBal && (
                  <div className="text-right text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Saldo posterior</span>
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {formatCurrency(isCC ? Math.abs(txBal.next ?? txBal.curr ?? 0) : (txBal.next ?? txBal.curr ?? 0), account?.currency)}
                    </span>
                  </div>
                )}
              </div>

              {/* Lista de Acciones */}
              <div className="space-y-1.5 pt-1">
                {/* Editar */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.currentTarget?.blur();
                    close();
                    setTimeout(() => handleEditTransaction(tx), 240);
                  }}
                  className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                    <Edit2 className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">Editar Movimiento</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Modificar monto, concepto, fecha o categoría</p>
                  </div>
                </button>

                {/* Alternar Desglose (Solo tarjetas) */}
                {isCC && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      close();
                      handleToggleBreakdown(tx.id);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                      <ArrowLeftRight className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        {tx.is_breakdown ? 'Cambiar a Compra Normal' : 'Marcar como Desglose'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {tx.is_breakdown ? 'Remover del progreso de desglose' : 'Incluir en la atribución de deuda'}
                      </p>
                    </div>
                  </button>
                )}

                {/* Liberar Retención */}
                {isRetention && !isReleased && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      close();
                      handleReleaseRetention(tx.id);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                      <Unlock className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Liberar Retención</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Desbloquear el monto y reintegrarlo a tu disponible</p>
                    </div>
                  </button>
                )}

                {/* Eliminar */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.currentTarget?.blur();
                    close();
                    setTimeout(() => {
                      setTransactionToDelete(tx);
                      setShowDeleteConfirm(true);
                    }, 240);
                  }}
                  className="w-full p-3 rounded-2xl hover:bg-red-50/60 dark:hover:bg-red-950/30 border border-red-200/60 dark:border-red-900/40 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-red-50 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">Eliminar Movimiento</p>
                    <p className="text-xs text-red-500/80 truncate">Borrar de forma permanente y actualizar saldo</p>
                  </div>
                </button>
              </div>
            </div>
          );
        }}
      </MobileBottomSheet>

      {/* ─── MODAL BOTTOM-SHEET: FILTROS Y BÚSQUEDA ─── */}
      <MobileBottomSheet
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
      >
        {({ close }) => (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Filter className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Filtrar Movimientos</h3>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Buscador de texto */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Buscar por concepto o notas</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ej: Súper, Netflix, Gasolina..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Tipo de Movimiento */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo de movimiento</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'all', label: 'Todos' },
                  { id: 'expense', label: 'Gastos' },
                  { id: 'income', label: 'Ingresos' }
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFilterType(t.id)}
                    className={`py-2 px-3 rounded-xl text-xs font-bold border transition-all ${
                      filterType === t.id
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Criterio de Ordenación */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ordenar por</label>
              <div className="grid grid-cols-1 gap-1.5">
                {[
                  { id: 'date-desc', label: 'Fecha: Más reciente primero' },
                  { id: 'date-asc', label: 'Fecha: Más antiguo primero' },
                  { id: 'amount-desc', label: 'Importe: Mayor a menor ($)' },
                  { id: 'amount-asc', label: 'Importe: Menor a mayor ($)' }
                ].map(o => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSortBy(o.id)}
                    className={`py-2.5 px-3.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-between ${
                      sortBy === o.id
                        ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/60 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <span>{o.label}</span>
                    {sortBy === o.id && <Check className="w-4 h-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Botones de Acción */}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setSortBy('date-desc');
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold active:scale-95 transition-all"
              >
                Restablecer
              </button>
              <button
                type="button"
                onClick={close}
                className="flex-[2] py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-md shadow-blue-200 dark:shadow-none active:scale-95 transition-all"
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        )}
      </MobileBottomSheet>

      {/* ─── MODAL BOTTOM-SHEET: DESCARGA / EXPORTACIÓN ─── */}
      <MobileBottomSheet
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      >
        {({ close }) => (
          <div className="space-y-3.5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Download className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Descargar Movimientos</h3>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Elige el formato preferido para descargar el historial de <strong>{account?.name}</strong>.
            </p>

            <div className="space-y-2 pt-1">
              {/* PDF */}
              <button
                type="button"
                onClick={() => handleExport('pdf')}
                className="w-full p-3.5 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
              >
                <div className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Documento PDF (.pdf)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Formato formal e imprimible con resumen y balance</p>
                </div>
                <Download className="w-4 h-4 text-slate-400 shrink-0" />
              </button>

              {/* Excel */}
              <button
                type="button"
                onClick={() => handleExport('excel')}
                className="w-full p-3.5 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Hoja de Cálculo Excel (.xls)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Datos estructurados por columnas listos para cálculo</p>
                </div>
                <Download className="w-4 h-4 text-slate-400 shrink-0" />
              </button>

              {/* CSV */}
              <button
                type="button"
                onClick={() => handleExport('csv')}
                className="w-full p-3.5 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Archivo Plano CSV (.csv)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Texto separado por comas compatible con cualquier aplicación</p>
                </div>
                <Download className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            </div>
          </div>
        )}
      </MobileBottomSheet>
    </Layout>
  );
};

export default AccountDetails;
